/*----------------------------------
- HOME ASK AI SECTION
- Non-sticky "Ask AI about Unique Domains" block placed just before the footer.
- Matches the ona.com pattern: heading + a row of official AI-provider marks
- that open the page's context prompt in each assistant.
----------------------------------*/

import React from 'react';

import { track } from '@/client/pages/landing/lib/analytics';
import { PAGE_CONTAINER_CLASS } from '@/client/pages/landing/components/shared/pageStyles';
import { buildAskYourAIPrompt, type AskYourAIProps } from '@uniquedomains/components/ask-ai';
import { ASK_AI_PROVIDERS, buildAskAiProviderHref, resolveAskAiIconSrc } from '@uniquedomains/components/ask-ai/providers';

/*----------------------------------
- COMPONENT
----------------------------------*/

const HomeAskAiSection = ({ context }: { context: AskYourAIProps }) => {
    const prompt = React.useMemo(() => buildAskYourAIPrompt(context), [context]);

    return (
        <section class={`${PAGE_CONTAINER_CLASS} pb-2 pt-14 lg:pt-16`}>
            <div data-reveal class="flex flex-col items-center gap-[18px] text-center lg:gap-6">
                <p class="text-sm font-medium tracking-[-0.01em] text-slate-600 lg:text-[15px]">Ask AI about Unique Domains</p>

                <div class="flex items-center gap-6 lg:gap-8">
                    {ASK_AI_PROVIDERS.map((provider) => (
                        <a
                            key={provider.id}
                            href={buildAskAiProviderHref(provider, prompt)}
                            target="_blank"
                            rel="noreferrer"
                            onClick={() => track('ask_ai_menu_opened', { provider: provider.id, placement: 'home_footer_ask_ai' })}
                            aria-label={`Ask ${provider.label} about Unique Domains`}
                            title={`Ask ${provider.label}`}
                            /* 44px of tappable area around a 28px mark on a phone; the desktop row
                               keeps the mark's own box so the spacing there is unchanged. */
                            class="group inline-flex h-11 w-11 items-center justify-center lg:h-auto lg:w-auto"
                        >
                            <img
                                src={resolveAskAiIconSrc(provider.icon)}
                                alt=""
                                aria-hidden="true"
                                width="28"
                                height="28"
                                loading="lazy"
                                decoding="async"
                                class="h-7 w-7 opacity-40 transition duration-200 group-hover:-translate-y-0.5 group-hover:opacity-100"
                            />
                        </a>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default HomeAskAiSection;
